/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar, LuisCommandBarProps } from 'src/core/components/LuisCommandBar';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { CustomSkillDeploymentStore } from 'src/core/stores/CustomSkillDeploymentStore';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';

export type DeployedModelsTableCommandBarProps = Partial<LuisCommandBarProps> &
    TableCommandBarProps<CustomSkillDeploymentStore | CustomSkillDeploymentStore> & {
        noneKeys?: string[];
        onDeploy: () => void;
        deployMessage?: string;
        onExportModel?: () => void;
        onSwapDeployments?: () => void;
        onGetPredictionUrl: () => void;
        onDeleteDeployment?: () => void;
        isAddDeploymentButtonDisabled?: boolean;
    };

const messages = defineMessages({
    export: { id: 'modelDeploy.deployedModelsTableCommandBar.export', defaultMessage: 'Export model' },
    deployModel: { id: 'modelDeploy.deployedModelsTableCommandBar.deployModel', defaultMessage: 'Add deployment' },
    searchPlaceholder: { id: 'modelDeploy.deployedModelsTableCommandBar.searchPlaceholder', defaultMessage: 'Search' },
    swapDeployments: { id: 'modelDeploy.deployedModelsTableCommandBar.swapDeployments', defaultMessage: 'Swap deployments' },
    deleteDeployment: { id: 'modelDeploy.deployedModelsTableCommandBar.deleteDeployment', defaultMessage: 'Delete deployment' },
    getPredictionUrl: { id: 'modelDeploy.deployedModelsTableCommandBar.getPredictionUrl', defaultMessage: 'Get prediction URL' }
});

export const DeployedModelsTableCommandBar = observer((props: DeployedModelsTableCommandBarProps) => {
    const {
        query,
        onDeploy,
        selectedItems,
        deployMessage,
        onQueryChange,
        onExportModel,
        onSwapDeployments,
        onDeleteDeployment,
        onGetPredictionUrl,
        includeSearchBar = true,
        isAddDeploymentButtonDisabled = false,
        noneKeys = ['deployModel'],
        ...rest
    } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canDeployProject } = mapUserRoleToUserActions(userRole);

    const items: ICommandBarItemProps[] = [
        {
            key: 'deployModel',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deployModelButtonLanguageToolTip"
                    hidden={canDeployProject || item.disabled || isAddDeploymentButtonDisabled}
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            onClick={onDeploy}
                            iconProps={{ iconName: 'Deploy' }}
                            name={deployMessage ?? localize(messages.deployModel)}
                            text={deployMessage ?? localize(messages.deployModel)}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            disabled={!canDeployProject || item.disabled || isAddDeploymentButtonDisabled}
                        />
                    }
                />
            )
        },
        {
            key: 'getPredictionURL',
            onClick: onGetPredictionUrl,
            iconProps: { iconName: 'Globe' },
            name: localize(messages.getPredictionUrl)
        },
        ...(onSwapDeployments
            ? [
                  {
                      key: 'swapDeployments',
                      onRender: (item: ICommandBarItemProps) => (
                          <RBACTooltip
                              displayedUserRole={displayedUserRole}
                              hidden={canDeployProject || item.disabled || selectedItems.length !== 2}
                              data-automation-id="swapDeploymentsButtonLanguageToolTip"
                              componentSurroundedByToolTip={
                                  <CommandBarButton
                                      onClick={onSwapDeployments}
                                      iconProps={{ iconName: 'Rename' }}
                                      name={localize(messages.swapDeployments)}
                                      text={localize(messages.swapDeployments)}
                                      styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                                      disabled={item.disabled || !canDeployProject || selectedItems.length !== 2}
                                  />
                              }
                          />
                      )
                  }
              ]
            : []),
        ...(onDeleteDeployment
            ? [
                  {
                      key: 'deleteDeployment',
                      onRender: (item: ICommandBarItemProps) => (
                          <RBACTooltip
                              displayedUserRole={displayedUserRole}
                              hidden={canDeployProject || item.disabled}
                              data-automation-id="deleteDeploymentModelButtonLanguageToolTip"
                              componentSurroundedByToolTip={
                                  <CommandBarButton
                                      onClick={onDeleteDeployment}
                                      iconProps={{ iconName: 'Delete' }}
                                      name={localize(messages.deleteDeployment)}
                                      text={localize(messages.deleteDeployment)}
                                      disabled={item.disabled || !canDeployProject}
                                      styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                                  />
                              }
                          />
                      )
                  }
              ]
            : [])
    ];

    const commandBarItems: ICommandBarItemProps[] = commandBarItemSelectionManager(
        items,
        noneKeys,
        ['getPredictionURL', 'deleteDeployment'],
        ['swapDeployments'],
        selectedItems?.length || 0
    );

    return (
        <LuisCommandBar
            query={query}
            searchBarAlwaysExpanded
            items={commandBarItems}
            onQueryChange={onQueryChange}
            includeSearchBar={includeSearchBar}
            {...rest}
        />
    );
});
